import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { EditorModule } from 'primeng/editor';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { catchError, forkJoin, map, of } from 'rxjs';
import { Country } from '../../models/common/country.model';
import { Customer } from '../../customer/customer.model';
import { CustomerService } from '../../customer/customer.service';
import { PartyQuickCreateDialogComponent, PartyQuickCreateResult } from '../../shared/party-quick-create/party-quick-create-dialog.component';
import { LazyLocationPickerDirective } from '../../shared/location-picker/lazy-location-picker.directive';
import { Location } from '../../location/location.model';
import { LocationService } from '../../location/location.service';
import { SessionStorageService } from '../../services/session-storage.service';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PartyService } from '../../party/party.service';
import { HrModuleNavComponent } from '../shared/hr-module-nav.component';
import { HrBiometricProviderApp } from '../biometric/hr-biometric.models';
import { HrBiometricService } from '../biometric/hr-biometric.service';
import {
  HR_EMPLOYEE_STATUSES,
  HR_EMPLOYEE_TYPES,
  HrDepartment,
  HrEmployee,
  HrJob,
  HrPosition
} from '../hr.models';
import { HrService } from '../hr.service';

@Component({
  selector: 'app-hr-employee-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterLink,
    ButtonModule,
    DialogModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputTextModule,
    EditorModule,
    ProgressBarModule,
    SelectModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageFormSectionComponent,
    PageDataPanelComponent,
    HrModuleNavComponent,
    PartyQuickCreateDialogComponent,
    LazyLocationPickerDirective
  ],
  providers: [MessageService],
  templateUrl: './hr-employee-form.component.html',
  styleUrls: ['./hr-employee-form.component.scss']
})
export class HrEmployeeFormComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly hrService = inject(HrService);
  private readonly customerService = inject(CustomerService);
  private readonly partyService = inject(PartyService);
  private readonly locationService = inject(LocationService);
  private readonly biometricService = inject(HrBiometricService);
  private readonly messageService = inject(MessageService);
  private readonly sessionStorageService = inject(SessionStorageService);

  employeeId?: number;
  loading = false;
  saving = false;
  linkedEmployeeDialogVisible = false;
  linkedCustomerDialogVisible = false;
  linkedCustomerProfile: Customer | null = null;

  customers: Customer[] = [];
  departments: HrDepartment[] = [];
  jobs: HrJob[] = [];
  positions: HrPosition[] = [];
  managers: HrEmployee[] = [];
  locations: Location[] = [];
  biometricProviders: HrBiometricProviderApp[] = [];
  readonly countries: Country[] = this.sessionStorageService.getCountryList() ?? [];
  readonly owningCompany = this.sessionStorageService.getCompany() ?? null;

  categoryOptions: { label: string; value: string }[] = [];
  readonly typeOptions = HR_EMPLOYEE_TYPES.map(value => ({ label: this.format(value), value }));
  readonly statusOptions = HR_EMPLOYEE_STATUSES.map(value => ({ label: this.format(value), value }));

  readonly form = this.fb.group({
    employeeNumber: [''],
    firstName: [''],
    lastName: [''],
    displayName: ['', Validators.required],
    emailAddress: ['', Validators.email],
    phoneNumber: [''],
    customerId: [null as number | null],
    imageUrl: [''],
    workerCategory: ['OFFICE', Validators.required],
    employeeType: ['FULL_TIME', Validators.required],
    status: ['DRAFT', Validators.required],
    departmentId: [null as number | null],
    jobId: [null as number | null],
    positionId: [null as number | null],
    managerEmployeeId: [null as number | null],
    workLocationId: [null as number | null],
    workLocation: [''],
    startDate: [''],
    endDate: [''],
    notes: [''],
    biometricProviderCode: [''],
    biometricEmployeeIdentifierKey: ['']
  });

  get departmentOptions() {
    return this.departments.map(item => ({ label: item.departmentName || `Department ${item.departmentId}`, value: item.departmentId }));
  }

  get jobOptions() {
    return this.jobs.map(item => ({ label: item.jobName || `Job ${item.jobId}`, value: item.jobId }));
  }

  get positionOptions() {
    return this.positions.map(item => ({ label: item.positionName || `Position ${item.positionId}`, value: item.positionId }));
  }

  get customerOptions() {
    return this.customers
      .sort((left, right) => (left.name || '').localeCompare(right.name || ''))
      .map(item => ({
        label: this.customerLabel(item),
        value: item.id,
        emailAddress: item.emailAddress || '',
        phoneNumber: item.phoneNumber || ''
      }));
  }

  get locationOptions() {
    return this.locations
      .slice()
      .sort((left, right) => (left.name || '').localeCompare(right.name || ''))
      .map(item => ({
        label: this.locationLabel(item),
        value: item.id
      }));
  }

  get biometricProviderOptions() {
    return this.biometricProviders
      .slice()
      .sort((left, right) => (left.displayName || left.providerCode || '').localeCompare(right.displayName || right.providerCode || ''))
      .map(item => ({
        label: item.displayName || item.providerCode,
        value: item.providerCode
      }));
  }

  get managerOptions() {
    return this.managers
      .filter(item => item.employeeId !== this.employeeId)
      .map(item => ({ label: `${item.displayName || 'Employee'} (${item.employeeNumber || item.employeeId})`, value: item.employeeId }));
  }

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    const companyId = this.sessionStorageService.getCompany()?.id ?? this.sessionStorageService.getCustomer()?.company?.id;
    this.employeeId = id ? Number(id) : undefined;
    this.loading = true;
    const baseLoad = forkJoin({
      customers: companyId
        ? this.partyService.getParties(companyId, ['Employee'], '', 0, 1000).pipe(
          map(response => (response.content || []).map(party => this.partyService.convertPartyToCustomer(party))),
          catchError(() => of([] as Customer[]))
        )
        : of([] as Customer[]),
      locations: companyId
        ? this.locationService.getLocationsByCompanyId(0, 25, companyId).pipe(
          map(response => response.items || []),
          catchError(() => of([] as Location[]))
        )
        : of([] as Location[]),
      biometricProviders: this.biometricService.marketplaceApps().pipe(catchError(() => of([] as HrBiometricProviderApp[]))),
      departments: this.hrService.departments(),
      jobs: this.hrService.jobs(),
      positions: this.hrService.positions(),
      workerCategories: this.hrService.workerCategories(),
      managers: this.hrService.employees({ page: 0, pageSize: 200 })
    });
    baseLoad.subscribe({
      next: response => {
        this.customers = response.customers ?? [];
        this.locations = response.locations ?? [];
        this.biometricProviders = response.biometricProviders ?? [];
        this.departments = response.departments.data ?? [];
        this.jobs = response.jobs.data ?? [];
        this.positions = response.positions.data ?? [];
        this.categoryOptions = (response.workerCategories.data ?? []).map(item => ({
          label: item.categoryName || this.format(item.categoryCode), value: item.categoryCode || ''
        }));
        this.managers = response.managers.items ?? [];
        if (this.employeeId) {
          this.loadEmployee(this.employeeId);
        }
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load HR setup data.' });
      },
      complete: () => {
        if (!this.employeeId) {
          this.loading = false;
        }
      }
    });
  }

  loadEmployee(employeeId: number): void {
    this.hrService.employee(employeeId).subscribe({
      next: response => {
        const employee = response.data;
        if (employee) {
          this.form.patchValue({
            employeeNumber: employee.employeeNumber || '',
            firstName: employee.firstName || '',
            lastName: employee.lastName || '',
            displayName: employee.displayName || '',
            emailAddress: employee.emailAddress || '',
            phoneNumber: employee.phoneNumber || '',
            customerId: employee.customerId ?? null,
            imageUrl: employee.imageUrl || '',
            workerCategory: employee.workerCategory || 'OFFICE',
            employeeType: employee.employeeType || 'FULL_TIME',
            status: employee.status || 'DRAFT',
            departmentId: employee.departmentId ?? null,
            jobId: employee.jobId ?? null,
            positionId: employee.positionId ?? null,
            managerEmployeeId: employee.managerEmployeeId ?? null,
            workLocationId: employee.workLocationId ?? null,
            workLocation: employee.workLocation || '',
            startDate: employee.startDate || '',
            endDate: employee.endDate || '',
            notes: employee.notes || '',
            biometricProviderCode: employee.biometricProviderCode || '',
            biometricEmployeeIdentifierKey: employee.biometricEmployeeIdentifierKey || ''
          });
          if (!employee.workLocationId && employee.workLocation) {
            const matchedLocation = this.locations.find(location => location.name === employee.workLocation);
            if (matchedLocation?.id) {
              this.form.patchValue({ workLocationId: matchedLocation.id });
            }
          }
          if (employee.customerId) {
            this.ensureCustomerLoaded(employee.customerId);
          }
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load employee.' }),
      complete: () => this.loading = false
    });
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.saving = true;
    const request = this.clean(this.form.getRawValue() as HrEmployee);
    const save$ = this.employeeId
      ? this.hrService.updateEmployee(this.employeeId, request)
      : this.hrService.createEmployee(request);
    save$.subscribe({
      next: response => {
        const savedId = response.data?.employeeId || this.employeeId;
        this.messageService.add({ severity: 'success', summary: 'HR', detail: 'Employee saved.' });
        if (savedId) {
          this.router.navigate(['/hr/employees', savedId]);
        }
      },
      error: () => this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to save employee.' }),
      complete: () => this.saving = false
    });
  }

  openLinkedEmployeeCreator(): void {
    if (!this.owningCompany) {
      this.messageService.add({ severity: 'warn', summary: 'HR', detail: 'Select a tenant company before creating a linked employee party.' });
      return;
    }
    this.linkedEmployeeDialogVisible = true;
  }

  onLinkedEmployeeCreated(result: PartyQuickCreateResult): void {
    const created = result.record as Customer;
    if (created?.id) {
      this.form.patchValue({ customerId: created.id });
      this.ensureCustomerLoaded(created.id);
    }
    this.linkedEmployeeDialogVisible = false;
  }

  onLinkedEmployeeCreationFailed(message: string): void {
    this.messageService.add({ severity: 'error', summary: 'HR', detail: message || 'Unable to create linked portal employee.' });
  }

  viewLinkedCustomerProfile(): void {
    const customerId = this.form.get('customerId')?.value;
    if (!customerId) {
      return;
    }
    this.linkedCustomerDialogVisible = true;
    this.linkedCustomerProfile = null;
    this.customerService.getCustomerById(customerId).subscribe({
      next: response => {
        this.linkedCustomerProfile = response.data || null;
      },
      error: () => {
        this.linkedCustomerDialogVisible = false;
        this.messageService.add({ severity: 'error', summary: 'HR', detail: 'Unable to load the linked portal employee profile.' });
      }
    });
  }

  private ensureCustomerLoaded(customerId: number): void {
    if (this.customers.some(customer => customer.id === customerId)) {
      return;
    }
    this.customerService.getCustomerById(customerId).subscribe({
      next: response => {
        const customer = response.data;
        if (!customer) {
          return;
        }
        this.customers = [customer, ...this.customers.filter(item => item.id !== customer.id)];
      }
    });
  }

  format(value?: string): string {
    return (value || '').toLowerCase().split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  private customerLabel(customer: Customer): string {
    const name = customer.name || `Customer ${customer.id}`;
    const contact = customer.emailAddress || customer.phoneNumber;
    return contact ? `${name} - ${contact}` : name;
  }

  private locationLabel(location: Location): string {
    const name = location.name || `Location ${location.id}`;
    return location.externalLocationReference ? `${name} - ${location.externalLocationReference}` : name;
  }

  private clean(value: HrEmployee): HrEmployee {
    return Object.fromEntries(Object.entries(value).map(([key, entry]) => [key, entry === '' ? undefined : entry])) as HrEmployee;
  }
}
